import FormInput from "./FormInput";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useState, useEffect, useRef } from "react";
import { userAuthAPI } from "../api/userAuthAPI";
import { toast } from "react-toastify";
import useUserStore from "../zustand/UseUserStore";
import { SESSION_ID } from "../api/apiConfig";
import { showSuccessToast } from "../js/utils/toast";
import { showErrorToast } from "../js/utils/errors";
import { useCookieConsent } from "../context/CookieConsentContext";

const imagePath = import.meta.env.PROD
	? import.meta.env.VITE_PROD_IMAGE_PATH
	: import.meta.env.VITE_DEV_IMAGE_PATH;
const clock = `${imagePath}/icon/clock.svg`;
const paperPlane = `${imagePath}/icon/paperPlane.svg`;

const Mobile2FA = ({
	onSubmit = () => {},
	control,
	newUser,
	...otherProps
}) => {
	const [timer, setTimer] = useState(null);
	const [phone, setPhone] = useState("");
	const [code, setCode] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	const cleanedPhone = useRef("");
	const { onShowCookieConsent } = useCookieConsent();

	const state = useUserStore();

	// Handles phone number submission
	async function handleSendPhone() {
		if (!localStorage.getItem("cookiesaccepted")) {
			setError("Cookies not accepted.");
			onShowCookieConsent(); // Show the cookie consent component
			toast.error("Please accept cookies to sign in or sign up.");
			return;
		}
		const sessionid = SESSION_ID();
		try {
			const response = await userAuthAPI.getphonecode(
				phone.replace(/\D/g, ""),
				sessionid
			);
			console.log("Create login attempt response:", response);
			setLoading(false);
			state.setLoginAttemptId(response.result.id);
			toast.success("Code sent successfully!");
		} catch (error) {
			setError("No account associated with this phone number.");
		}
	}

	// Handles code submission
	async function handleVerifyPhone(code, phone) {
		// Prevent duplicate calls
		if (loading) {
			return;
		}

		setLoading(true);

		try {
			const response = await userAuthAPI.verifyphonecode(
				state.loginAttemptId,
				code,
				phone,
				state.user.CreatureID
			);
			handleGetAccount(response.result.memberID);
		} catch (error) {
			setTimer(0);
			setLoading(false);
			setError(error.response.data.message);
			console.log(error);
		}
	}

	// Handles account retrieval
	const handleGetAccount = async (userID) => {
		try {
			// Send to API
			const response = await userAuthAPI.getaccount(userID);
			state.setUserWithRoles(
				response.result.member,
				response.result.user_roles
			);
			if (response.result.avatar) {
				state.buildCharacter(response.result.avatar);
			}

			// Use the reusable method from the store
			const userProgress = state.setupUserProgressFromAccount(response);

			if (userProgress.onboardingStep >= 3) {
				showSuccessToast("Sign in successful!");
			} else {
				showSuccessToast("Phone verification successful!");
			}

			onSubmit();
		} catch (error) {
			console.error("Error getting avatar:", error);
			showErrorToast("Failed to get avatar." + error);
		}
	};

	// Handles timer countdown and cleanup
	useEffect(() => {
		if (timer !== null && timer > 0) {
			const timeoutId = setTimeout(() => {
				setTimer((prevTimer) => prevTimer - 1);
			}, 1000);
			return () => clearTimeout(timeoutId);
		}
	}, [timer]);

	// Handles reset timer on new login attempt
	useEffect(() => {
		if (state.loginAttemptId) {
			setTimer(65);
		}
	}, [state.loginAttemptId]);

	// Handles phone number input formatting
	function cleanPhoneNumber(phoneNum) {
		const numbers = phoneNum.replace(/\D/g, "");

		if (numbers.length >= 10) {
			const output =
				numbers.substring(0, 3) +
				"-" +
				numbers.substring(3, 6) +
				"-" +
				numbers.substring(6, 10);
			return output;
		}
		return numbers;
	}

	const getTimerPrompt = () => {
		// Show code sent prompt for first 60-5=5 seconds
		if (timer > 60) {
			return (
				<p className="my-4 rounded-md border border-emerald-300 bg-emerald-100/90 p-2 text-center text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100">
					Code sent to {cleanedPhone.current}!
				</p>
			);
		} else if (timer > 0) {
			// Show timer from 60 to 1
			return (
				<p className="mx-auto mt-4 flex max-w-max items-center border-y border-blue-400 px-2 py-1 text-gray-700 dark:border-blue-500 dark:text-gray-200">
					<img src={clock} alt="Clock icon" className="mr-2 h-5 w-5" /> Retry in{" "}
					{timer} seconds.
				</p>
			);
		} else {
			// Show resend button when timer reaches 0
			return (
				<Button
					type="button"
					variant="outline"
					className="border-2 border-blue-400 text-blue-500 hover:bg-blue-50 dark:border-blue-500 dark:text-blue-400 dark:hover:bg-blue-900/30"
					onClick={() => {
						handleSendPhone(phone);
					}}
				>
					<img src={paperPlane} alt="Paper plane icon" className="h-5 w-5" />
					Send another code
				</Button>
			);
		}
	};

	// Helper function to check if device is desktop (not touch device)
	const isDesktop = () => {
		return (
			window.innerWidth >= 768 &&
			!("ontouchstart" in window || navigator.maxTouchPoints > 0)
		);
	};

	// Helper function to handle Enter key press on desktop
	const handleEnterKey = (e, callback) => {
		if (e.key === "Enter" && !e.shiftKey && isDesktop()) {
			e.preventDefault();
			callback();
		}
	};

	// Use prop handleSendPhone if provided, otherwise use internal one
	const sendPhoneHandler = otherProps.handleSendPhone || handleSendPhone;

	const PhoneInput = (
		<div className="flex gap-2">
			{/* Phone Input */}
			<Input
				type="tel"
				id="phone"
				name="phone"
				placeholder="Enter phone number"
				value={phone}
				inputMode="numeric"
				className="rounded-xl border-2 border-gray-400 py-4 text-base focus:border-blue-500 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:focus:border-blue-400 sm:py-6 sm:text-lg"
				onBlur={(e) => {
					setPhone(cleanedPhone.current);
				}}
				onChange={(e) => {
					const cleanedValue = cleanPhoneNumber(e.target.value);
					cleanedPhone.current = cleanedValue;
					setPhone(e.target.value);
				}}
				onKeyDown={(e) => {
					handleEnterKey(e, () => {
						if (cleanedPhone.current.replace(/\D/g, "").length >= 10) {
							// Use prop handler if it exists, otherwise use internal one
							if (otherProps.handleSendPhone) {
								otherProps.handleSendPhone(
									cleanedPhone.current.replace(/\D/g, "")
								);
							} else {
								handleSendPhone();
							}
						}
					});
				}}
			/>

			{/* Send Code Button */}
			{cleanedPhone.current.replace(/\D/g, "").length >= 10 && (
				<Button
					type="button"
					onClick={() => {
						if (otherProps.handleSendPhone) {
							otherProps.handleSendPhone(
								cleanedPhone.current.replace(/\D/g, "")
							);
						} else {
							handleSendPhone();
						}
					}}
					className="rounded-xl bg-blue-500 px-4 py-4 text-base text-white hover:bg-blue-600 sm:px-6 sm:py-6 sm:text-lg"
				>
					Send Code
				</Button>
			)}
		</div>
	);

	const CodeInput = (
		<div className="flex gap-2">
			{/* Code Input */}
			<Input
				name="code"
				inputMode="numeric"
				pattern="[0-9]*"
				placeholder="Enter verification code"
				value={code}
				className="rounded-xl border-2 border-gray-400 py-4 text-base focus:border-blue-500 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:focus:border-blue-400 sm:py-6 sm:text-lg"
				onChange={(e) => {
					setCode(e.target.value);
				}}
				onKeyDown={(e) => {
					// Only handle Enter if we're in code input mode (loginAttemptId exists)
					if (state.loginAttemptId) {
						handleEnterKey(e, () => {
							if (code.length > 0 && !loading) {
								handleVerifyPhone(code, phone);
							}
						});
					}
				}}
			/>

			{/* Submit Button */}
			{code.length > 0 && (
				<>
					{" "}
					{loading ? (
						<Button
							type="button"
							className="rounded-xl px-4 py-4 text-base sm:px-6 sm:py-6 sm:text-lg"
							disabled
						>
							Loading...
						</Button>
					) : (
						<Button
							type="button"
							className="rounded-xl bg-blue-500 px-4 py-4 text-base text-white hover:bg-blue-600 sm:px-6 sm:py-6 sm:text-lg"
							onClick={() => {
								if (!loading) {
									handleVerifyPhone(code, phone);
								}
							}}
							disabled={loading}
						>
							Continue
						</Button>
					)}
				</>
			)}
		</div>
	);

	return (
		<>
			<div className="mx-auto flex w-full flex-col gap-2">
				{/* Input */}
				{state.loginAttemptId ? CodeInput : PhoneInput}

				{/* Code timer prompt and controls - Only show if timer started */}
				{timer !== null && getTimerPrompt()}

				{/* Error message */}
				{error && (
					<p className="m-1 text-left text-sm text-red-600 dark:text-red-400">
						{error}
					</p>
				)}

				{/* Go Back button - only show when on code verification step */}
				{state.loginAttemptId && (
					<Button
						variant="ghost"
						type="button"
						onClick={() => {
							// Clear loginAttemptId to go back to phone input
							state.setLoginAttemptId(null);
							setCode("");
							setError("");
							setTimer(null);
						}}
						className="mt-2 w-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
					>
						Go Back
					</Button>
				)}
			</div>
		</>
	);
};

export default Mobile2FA;
